import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { CoordinatorService } from 'coordinator';
import { firstValueFrom } from 'rxjs';
import { Config, ConfigSection } from 'dist/coordinator/lib/config$';

@Injectable({
    providedIn: 'root',
})
export class ConfigService {
    private readonly storage: Storage = inject(Storage);

    constructor(public coords: CoordinatorService) {}

    async getRemoteConfig(http: HttpClient): Promise<void> {
        try {
            const storageRef = ref(this.storage, `config/app_config.json`);
            const configFileUrl = await getDownloadURL(storageRef);

            console.log("CONFIG URL", configFileUrl)
            const configData = (await firstValueFrom(
                http.get(configFileUrl, { responseType: 'json' }),
            )) as Config;
            console.log(`Remote App Config fetched successfully, applying... `);
            this.setConfig(configData);
        } catch (err) {
            this.coords.utils$.consoleError(
                'Error fetching app config, will use fallback where possible: ',
                err,
            );
            this.setConfig(null);
        }
    }

    setConfig(configData: { [key in ConfigSection]?: any } | null) {
        let config = configData;
        console.log(`Applying config:`, config);
        if (!config) {
            console.warn('No config data provided, using fallback/default data');
            config = this.fallbackConfigData;
        }

        this.coords.config$.setConfig(config);
    }

    private get fallbackConfigData() {
        return {
            parsers: {
                wos: {
                    fields: {
                        AB: 'abstract',
                        AN: 'articleNumber',
                        AU: 'authorLs:list',
                        BE: 'editorLs:list',
                        BN: 'isbnLs:list',
                        BP: 'startPage',
                        CT: 'confTitle',
                        CP: 'confTitle',
                        CY: 'confYear',
                        CL: 'confLocation',
                        DT: 'documentType',
                        DI: 'doi',
                        DO: 'doi',
                        EI: 'eissnLs:list',
                        EP: 'endPage',
                        IS: 'issue',
                        JI: 'sourceAbr',
                        J2: 'sourceAbr',
                        LA: 'langLs:list',
                        LB: 'late_priority',
                        LC: 'earliestPriority',
                        MA: 'meeting_ab',
                        M3: 'documentType',
                        OI: 'orcid',
                        PG: 'pageCount',
                        PI: 'publisherCity',
                        PN: 'patentNumber',
                        PU: 'publisher',
                        PY: 'year:parseYear',
                        SE: 'bookSeries',
                        SN: 'issnLs:list',
                        SP: 'startPage',
                        SO: 'source',
                        TI: 'title',
                        T2: 'source',
                        UT: 'id',
                        VL: 'volume',
                        ID: 'id',
                    },
                    params: {
                        tagWidth: 2,
                        valueStart: 3,
                        recordEnd: 'ER',
                    },
                    overrides: {
                        txt: {
                            SP: 'sponsors',
                        },
                    },
                },
                scifinder: {
                    fields: {
                        AU: 'authorLs:list',
                        IS: 'issue',
                        JF: 'source',
                        LA: 'langLs:list',
                        N1: 'id',
                        N2: 'abstract',
                        PY: 'year',
                        SP: 'startPage',
                        T1: 'title',
                        VL: 'volume',
                    },
                    params: {
                        tagWidth: 2,
                        valueStart: 6,
                        recordEnd: 'ER',
                    },
                },
                scopus: {
                    fields: {
                        AB: 'abstract',
                        AR: 'articleNumber',
                        AU: 'authorLs:list',
                        C7: 'articleNumber',
                        DB: 'dbName',
                        DO: 'doi',
                        EP: 'endPage',
                        IS: 'issue',
                        J2: 'sourceAbr',
                        M3: 'documentType',
                        PY: 'year:parseYear',
                        SN: 'issnLs:list',
                        SP: 'startPage',
                        T2: 'source',
                        TI: 'title',
                        UR: 'id:parseId',
                        VL: 'volume',
                        LA: 'langLs:list',
                    },
                    params: {
                        tagWidth: 2,
                        valueStart: 5,
                        recordEnd: 'ER',
                    },
                },
                pubmed: {
                    fields: {
                        AB: 'abstract',
                        DP: 'year:parseYear',
                        ED: 'editorLs:list',
                        TI: 'title',
                        BTI: 'title',
                        CTI: 'bookSeries',
                        VI: 'volume',
                        JT: 'source',
                        IP: 'issue',
                        IS: 'issnLs:list',
                        LA: 'langLs:list',
                        LID: 'doi:doi',
                        AID: 'doi:doi',
                        FAU: 'authorLs:list',
                        PB: 'publisher',
                        PMID: 'id',
                        PT: 'documentTypeLs:list',
                        PL: 'publisherCity',
                        PG: 'startPage:startPage',
                        TA: 'sourceAbr',
                        TT: 'origTitle',
                    },
                    params: {
                        tagWidth: 4,
                        valueStart: 6,
                        recordEnd: 'PMID',
                    },
                },
                espacenet: {
                    fields: {
                        Title: 'title',
                        Inventors: 'inventorLs',
                        Applicants: 'applicantLs',
                        'Publication number': 'id',
                        'Publication date': 'publicationDate',
                        'Earliest publication': 'earliestPublication',
                        'Earliest priority': 'earliestPriority',
                        IPC: 'ipc',
                        CPC: 'cpc',
                        'Family number': 'familyNumber',
                    },
                },
                universal: {
                    fields: {
                        TI: 'title',
                        T1: 'title',
                        AB: 'abstract',
                        N2: 'abstract',
                        AU: 'authorLs:list',
                        A1: 'authorLs:list',
                        JO: 'source',
                        VL: 'volume',
                        IS: 'issue',
                        SP: 'startPage',
                        EP: 'endPage',
                        PY: 'year:parseYear',
                        Y1: 'year:parseYear',
                        Y2: 'year:parseYear',
                        SN: 'issnLs:list',
                        DO: 'doi',
                    },
                    params: {
                        tagWidth: 2,
                        valueStart: 6,
                        recordEnd: 'ER',
                    },
                },
            },
            urls: {
                test_instructions:
                    'https://docs.google.com/spreadsheets/d/116DS-WC98Eqzd47I0sGsJTS7ksDmH44ntmQeZ5w8mw4/edit#gid=1161341563',
                help: 'https://referentka.notion.site/Help-8fa67b1d40f441758bfb129aa8c13529?pvs=4',
            },
            online: {
                crossref: {
                    averageScoreMultiplier: 1.0,
                },
            },
            footer: {
                copyright: 'Referentka © 2024',
                copyright_link: {
                    text: 'NTK',
                    url: 'https://techlib.cz',
                },
                realization: 'Realizace Big Dig Data s.r.o.',
                support_email: 'info@patentka.com',
                links: {
                    licenses: 'https://google.com',
                    privacy: 'https://google.com',
                    feedback: 'https://forms.google.com',
                },
            },
        };
    }
}
